<script setup lang="ts">
const { d, t } = useI18n()
const { reviews } = useReviewsQuery()

function getAuthorNameAlias(input: string) {
  if (!input || typeof input !== 'string')
    return ''

  const parts = input.trim().split(/\s+/)
  const filteredParts = parts.filter(part => part.length > 0)
  const selectedParts = filteredParts.slice(0, 2)

  const initials = selectedParts.map((part) => {
    const match = part.match(/[a-z]/i)
    return match ? match[0] : ''
  }).filter(initial => initial !== '')

  return initials.join('').toUpperCase()
}
</script>

<template>
  <div>
    <div v-if="reviews.status === 'pending'" class="flex flex-col animate-pulse gap-4 py-4">
      <div class="flex-none">
        <UiAvatar />
      </div>
      <div class="flex-1 space-y-2">
        <div class="flex items-center justify-between">
          <h3 class="font-semibold">
            <NSkeleton class="h-4 w-20 rounded" pulse />
          </h3>
          <p>
            <NSkeleton class="h-3 w-24 rounded" pulse />
          </p>
        </div>
        <div class="mt-1 flex items-center">
          <Rating :rating="0" class="-ml-0.5" />
        </div>
        <div class="prose-sm text-muted-foreground">
          <NSkeleton class="h-4 w-3/4 rounded" pulse />
        </div>
      </div>
    </div>

    <div v-else-if="reviews.status === 'error'" class="py-4 text-destructive font-semibold">
      <p>There was an error loading reviews.</p>
    </div>

    <div v-else class="flex flex-col space-y-4">
      <UiCard v-for="review in reviews.data?.items" :key="review.id">
        <UiCardContent class="flex gap-4 p-6 text-sm">
          <div class="flex-none">
            <UiAvatar>
              <UiAvatarFallback>
                {{ getAuthorNameAlias(review.authorName) }}
              </UiAvatarFallback>
            </UiAvatar>
          </div>

          <div class="flex-1 space-y-2">
            <div class="flex items-center justify-between">
              <h3 class="font-semibold">
                {{ review.authorName }}
              </h3>
              <time :datetime="review.createdAt" class="text-xs text-muted-foreground">
                {{ d(review.createdAt) }}
              </time>
            </div>

            <div class="flex items-center">
              <Rating :rating="review.rating" class="-ml-0.5" />
            </div>

            <p class="sr-only">
              {{ t('product.rating.x_out_of_y_stars.label', { x: review.rating, y: 5 }) }}
            </p>

            <div v-if="review.summary" class="text-foreground prose">
              {{ review.summary }}
            </div>

            <div v-if="review.body" class="text-muted-foreground prose">
              {{ review.body }}
            </div>
          </div>
        </UiCardContent>
      </UiCard>
    </div>
  </div>
</template>
